import { Button, Dropdown, H1, Input, Section, Space } from '@dnb/eufemia';
import { trash as TrashIcon } from '@dnb/eufemia/icons';
import { useEufemiaForm, useFieldArray } from '@portals/shared-frontend/hooks';
import { type JSX, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import slugify from 'slugify';
import { z } from 'zod';

import { saveTemplate } from '@/api/templates';
import Container from '@/components/Container';
import LoadingModal from '@/components/LoadingModal';

import style from './TemplateForm.module.css';

export enum SchemaType {
  string = 'string',
  markdown = 'markdown',
  boolean = 'boolean',
  text = 'text',
}

export const schemaForm = z.object({
  name: z.string().min(1),
  schema: z
    .object({
      name: z.string().min(1),
      type: z.string().and(z.enum(['string', 'markdown', 'boolean', 'text'])),
    })
    .array(),
});
export type SchemaForm = z.infer<typeof schemaForm>;

const DEFAULT_FORM: SchemaForm = {
  name: '',
  schema: [
    {
      name: '',
      type: SchemaType.string,
    },
  ],
};

export default function ContentFormByParent(): JSX.Element {
  const navigate = useNavigate();
  const {
    controller,
    controller: { setValue },
    register,
    handleSubmit,
    submitting,
  } = useEufemiaForm(schemaForm, {
    ...DEFAULT_FORM,
  });

  const schemaArray = useFieldArray(controller, 'schema');

  const onSubmit = handleSubmit(
    useCallback(
      async (data) => {
        await saveTemplate(data);
        navigate('..');
      },
      [navigate],
    ),
  );

  return (
    <Container centered size="small">
      {submitting && <LoadingModal />}

      <H1 bottom={false} top="large">
        New template
      </H1>
      <form onSubmit={onSubmit}>
        <Section backgroundColor="white" className={style['FormSet']} innerSpace={{ block: 'x-large' }}>
          <Space bottom="medium">
            <Input
              label="Schema title"
              size={40}
              stretch
              {...register.input('name')}
              onBlur={({ value }) => setValue('name', slugify(value, { lower: true }))}
            />
          </Space>

          <Space>
            {schemaArray.items.map(({ index, remove }) => (
              <Space bottom="small" className={style['FormSet']} key={`item-${index}`}>
                <Space>
                  <Input
                    label="Field name"
                    stretch
                    {...register.input(`schema.${index}.name`)}
                    onBlur={({ value }) => setValue(`schema.${index}.name`, slugify(value, { lower: true }))}
                  />
                </Space>
                <Space>
                  <Dropdown
                    data={SchemaType}
                    label="Field type"
                    {...register.dropdown(`schema.${index}.type`)}
                    top="small"
                  />
                </Space>
                <Space top="small">
                  <Button icon={TrashIcon} iconPosition="left" onClick={remove} variant="tertiary">
                    Remove
                  </Button>
                </Space>
              </Space>
            ))}
            <Button
              icon="add"
              iconPosition="left"
              onClick={() =>
                schemaArray.push({
                  name: '',
                  type: SchemaType.string,
                })
              }
              top="medium"
              variant="tertiary"
            >
              Add field
            </Button>
          </Space>
        </Section>
        <Section backgroundColor="white" innerSpace={{ block: 'small' }}>
          <Button disabled={submitting} icon="save" iconPosition="left" type="submit">
            Save changes
          </Button>
        </Section>
      </form>
    </Container>
  );
}
